"use client";
import { BuilderBlockProps } from "@/types";
import { isEditing } from "@builder.io/sdk-react";
import cn from "classnames";
import { FC, PropsWithChildren, useEffect, useRef, useState } from "react";

type ExpandableFormBlockProps = {
  maxVisibleFields?: number;
} & BuilderBlockProps;

/**
 * Wrapper for forms that initially hides extra fields and expands when the user
 * focuses any field. Shows only the first {maxVisibleFields} fields until expansion.
 *
 * **Important:** This component relies on form field symbols that have the
 * `builder-model="form-field"` attribute. The CSS selector uses this attribute
 * to count and hide fields beyond the visible limit.
 */
export const ExpandableFormBlock: FC<
  PropsWithChildren<ExpandableFormBlockProps>
> = ({ maxVisibleFields = 3, children, ...props }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const blockId = props.attributes["builder-id"];
  const editing = isEditing();
  const [isCollapsed, setIsCollapsed] = useState(!editing);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || editing) return;

    const handleFocusIn = () => {
      if (isCollapsed) {
        setIsCollapsed(false);
      }
    };

    wrapper.addEventListener("focusin", handleFocusIn);

    return () => {
      wrapper.removeEventListener("focusin", handleFocusIn);
    };
  }, [isCollapsed, editing]);

  const wrapperClass = `expandable-form-wrapper-${blockId}`;

  return (
    <>
      <style>
        {`
          .${wrapperClass} :nth-child(n+${maxVisibleFields + 1} of :has([builder-model="form-field"])) {
            max-height: 5000px;
            overflow: hidden;
            opacity: 1;
            transition: max-height 300ms ease-in, opacity 300ms ease-in;
          }
          
          .${wrapperClass}.collapsed :nth-child(n+${maxVisibleFields + 1} of :has([builder-model="form-field"])) {
            max-height: 0;
            opacity: 0;
          }
        `}
      </style>
      <div
        ref={wrapperRef}
        className={cn(
          wrapperClass,
          "overflow-hidden",
          isCollapsed && !editing ? "collapsed" : "expanded",
        )}
      >
        {children}
      </div>
    </>
  );
};
